import { ExpenseList } from '@/app/models/detailsRH';
import formatterMonetaryValue, { formatCurrency } from '@/app/utils/numberUtils';
import useFormattedCurrency from '@/hooks/useFormattedCurrency';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FormField from '../baseForms/FormField';
import InfoTotalValue from '../baseForms/info/InfoTotalValue';

type LabeledValueProps = {
  selectedItem: ExpenseList;
  onValueChange: (id: number, groupId: number, finalValue: number) => void;
};

export default function LabeledValue({ selectedItem, onValueChange }: LabeledValueProps) {
  const theme = useTheme();
  const { t } = useTranslation(['humanResource', 'common']);
  const difference = (selectedItem?.calculatedValue ?? 0) - (selectedItem?.expectedValue ?? 0);
  const { value: totalValue, setValue: setTotalValue } = useFormattedCurrency(selectedItem?.finalValue);

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const hasNegative = val.includes('-');
    const cleaned = val.replace(/[^0-9]/g, '');
    const raw = hasNegative ? `-${cleaned}` : cleaned;

    setTotalValue(formatCurrency(raw));

    onValueChange(selectedItem.id, selectedItem.expenseGroup.id, Number(raw) / 100);
  };

  return (
    <Box sx={{ mb: 2, mt: 2 }}>
      <Typography variant="fieldHeader" sx={{ width: theme.spacing(12), mb: 0, mt: 1 }}>
        {selectedItem.expenseGroup.name === 'Proventos' ? t('salary') : selectedItem.expenseGroup.name}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, maxWidth: theme.spacing(30) }}>
        <Box>
          <Typography sx={{ width: theme.spacing(12), mb: 0 }}>{t('common:system')}</Typography>
          <Stack direction="row" sx={{ width: '100%' }}>
            <Typography variant="subtitle2" sx={{ width: theme.spacing(12), mt: 2, fontSize: 15 }}>
              {formatterMonetaryValue(selectedItem?.calculatedValue ?? 0)}
            </Typography>
          </Stack>
        </Box>
        <Box>
          <Typography sx={{ width: theme.spacing(12), mb: 0 }}>{t('common:reason')}</Typography>
          <Stack direction="row" sx={{ width: '100%' }}>
            <Typography variant="subtitle2" sx={{ width: theme.spacing(12), mt: 2, fontSize: 15 }}>
              {formatterMonetaryValue(selectedItem?.expectedValue ?? 0)}
            </Typography>
          </Stack>
        </Box>
        <Box>
          <Typography sx={{ width: theme.spacing(12), mb: 0 }}>{t('common:difference')}</Typography>
          <Stack direction="row" sx={{ width: '100%' }}>
            <InfoTotalValue
              value={formatterMonetaryValue(difference)}
              sx={{ width: theme.spacing(25), mt: 1.1, ml: -4, alignItems: 'left' }}
              errorColor
            />
          </Stack>
        </Box>
        <Box>
          <Typography sx={{ width: theme.spacing(12) }}>{t('common:considered')}</Typography>
          <Stack direction="row" sx={{ width: '100%' }}>
            <FormField value={totalValue} onChange={handleValueChange} width={20} />
          </Stack>
        </Box>
      </Box>
      <Box sx={{ width: '100%', height: theme.spacing(0.25), bgcolor: 'grey.300', mt: 2, mb: 2 }} />
    </Box>
  );
}
